import { MetricTrackerConfig, createInternalConfig, InternalConfig } from './config';
import { PositionCodeEvent } from './types';
import { getOrCreateDeviceId } from '../utils/deviceId';
import { PositionCodeManager } from '../utils/positionCode';
import { UrlParamCollector } from '../collectors/UrlParamCollector';
import { ClickCollector } from '../collectors/ClickCollector';
import { Reporter } from '../reporter/Reporter';

/**
 * 指标追踪 SDK 主类
 */
export class MetricTracker {
  private config: InternalConfig;
  private deviceId: string;
  private positionCodeManager: PositionCodeManager;
  private urlParamCollector: UrlParamCollector;
  private clickCollector: ClickCollector;
  private reporter: Reporter; // 上报器
  private isStarted: boolean = false;
  private isInitialized: boolean = false;
  private lastPathname: string = '';
  private pendingPc: string | null = null;
  private effectiveRouteMode: 'history' | 'hash' = 'history';

  constructor(config: MetricTrackerConfig) {
    // 验证必填参数
    if (!config.appCode) {
      throw new Error('[MetricTracker] appCode is required');
    }
    if (!config.baseUrl) {
      throw new Error('[MetricTracker] baseUrl is required');
    }

    // 创建内部配置
    this.config = createInternalConfig(config);

    // 获取或生成设备ID
    this.deviceId = getOrCreateDeviceId();

    if (this.config.debug) {
      console.log('[MetricTracker] Initialized with config:', {
        appCode: this.config.appCode,
        baseUrl: this.config.baseUrl,
        deviceId: this.deviceId,
      });
    }

    // 初始化各个模块

    // 位置码管理器
    this.positionCodeManager = new PositionCodeManager(
      this.config.baseUrl,
      this.config.appCode,
      this.config.debug
    );

    // URL 参数收集器
    this.urlParamCollector = new UrlParamCollector(this.config.debug);

    // 点击事件收集器
    this.clickCollector = new ClickCollector(
      this.config.document,
      (event) => this.handleEvent(event),
      this.config.allowedDomains,
      (pc) => this.setPendingPc(pc),
      this.config.debug
    );

    // 上报器
    this.reporter = new Reporter(
      this.config.baseUrl,
      this.config.appCode,
      this.deviceId,
      this.config.userId,
      this.config.reportInterval,
      this.config.maxQueueSize,
      this.config.debug
    );
  }

  /**
   * 启动追踪
   */
  async start(): Promise<void> {
    if (this.isStarted) {
      if (this.config.debug) {
        console.log('[MetricTracker] Already started');
      }
      return;
    }

    this.isStarted = true;

    if (this.config.debug) {
      console.log('[MetricTracker] Starting...');
    }

    try {
      // 1. 确定有效的路由模式
      this.effectiveRouteMode = this.determineRouteMode();

      if (this.config.debug) {
        console.log('[MetricTracker] Route mode:', this.effectiveRouteMode);
      }

      // 2. 记录当前路径（用于后续路由变化检测）
      this.lastPathname = this.getCurrentPath();

      // 3. 先获取当前页面位置码并完成设置
      await this.updateCurrentPositionCode();

      // 4. 启动上报器（开始定时器）
      this.reporter.start();

      // 5. 启动点击事件收集器
      this.clickCollector.start();

      // 6. 监听路由变化（用于 SPA）
      this.setupRouteChangeListeners();

      // 7. 最后收集 URL 参数中的位置码
      // 此时位置码已经完全就绪，可以安全地收集和上报事件
      const urlEvent = this.urlParamCollector.collect();
      if (urlEvent) {
        this.handleEvent(urlEvent);
      }

      this.isInitialized = true;

      if (this.config.debug) {
        console.log('[MetricTracker] Started successfully');
      }
    } catch (error) {
      console.error('[MetricTracker] Failed to start:', error);
      this.isStarted = false;
      throw error;
    }
  }

  /**
   * 停止追踪
   */
  stop(): void {
    if (!this.isStarted) {
      if (this.config.debug) {
        console.log('[MetricTracker] Not started yet');
      }
      return;
    }

    if (this.config.debug) {
      console.log('[MetricTracker] Stopping...');
    }

    // 停止点击事件收集器
    this.clickCollector.stop();

    // 停止上报器（会自动上报剩余事件）
    this.reporter.stop();

    this.isStarted = false;
    this.isInitialized = false;

    if (this.config.debug) {
      console.log('[MetricTracker] Stopped');
    }
  }

  /**
   * 销毁实例
   */
  destroy(): void {
    this.stop();

    if (this.config.debug) {
      console.log('[MetricTracker] Destroyed');
    }
  }

  /**
   * 手动上报位置码事件
   * @param sourcePc 来源位置码
   */
  track(sourcePc: string): void {
    if (!this.isInitialized) {
      console.warn('[MetricTracker] Not initialized yet, cannot track');
      return;
    }

    if (!sourcePc) {
      console.warn('[MetricTracker] sourcePc is required');
      return;
    }

    const event: PositionCodeEvent = {
      sourcePc,
      eventTime: new Date(),
    };

    this.handleEvent(event);

    if (this.config.debug) {
      console.log('[MetricTracker] Manual track:', sourcePc);
    }
  }

  /**
   * 更新用户ID
   * @param userId 新的用户ID
   */
  setUserId(userId: string | undefined): void {
    this.config.userId = userId;
    // 同步更新 Reporter 中的 userId
    this.reporter.setUserId(userId);

    if (this.config.debug) {
      console.log('[MetricTracker] User ID updated:', userId);
    }
  }

  /**
   * 获取当前配置
   */
  getConfig(): Readonly<InternalConfig> {
    return { ...this.config };
  }

  /**
   * 获取设备ID
   */
  getDeviceId(): string {
    return this.deviceId;
  }

  /**
   * 设置待添加的 pc 参数
   */
  private setPendingPc(pc: string): void {
    this.pendingPc = pc;
    if (this.config.debug) {
      console.log('[MetricTracker] Pending pc set:', pc);
    }
  }

  /**
   * 确定有效的路由模式
   */
  private determineRouteMode(): 'history' | 'hash' {
    if (this.config.routeMode === 'history') {
      return 'history';
    }

    if (this.config.routeMode === 'hash') {
      return 'hash';
    }

    // auto 模式：自动检测
    const hash = window.location.hash;

    // 如果 hash 中包含斜杠（如 #/ 或 #/page），则认为是 hash 路由
    if (hash && hash.includes('/')) {
      return 'hash';
    }

    // 否则使用 history 模式
    return 'history';
  }

  /**
   * 获取当前路径（根据路由模式）
   */
  private getCurrentPath(): string {
    if (this.effectiveRouteMode === 'hash') {
      // hash 模式：使用 hash 部分（移除开头的 #）
      const hash = window.location.hash;
      return hash.startsWith('#') ? hash.substring(1) : hash;
    } else {
      // history 模式：使用 pathname
      return window.location.pathname;
    }
  }

  /**
   * 更新当前页面位置码
   */
  private async updateCurrentPositionCode(): Promise<void> {
    const currentPath = this.getCurrentPath();
    const currentPc = await this.positionCodeManager.getCurrentPositionCode(currentPath);
    this.reporter.setCurrentPositionCode(currentPc);
    this.clickCollector.setCurrentPositionCode(currentPc);

    if (this.config.debug) {
      console.log('[MetricTracker] Current position code updated:', currentPc, 'path:', currentPath);
    }
  }

  /**
   * 监听路由变化（用于 SPA）
   */
  private setupRouteChangeListeners(): void {
    if (this.effectiveRouteMode === 'hash') {
      // hash 模式：监听 hashchange 事件
      window.addEventListener('hashchange', () => {
        this.handleRouteChange('hashchange');
      });

      if (this.config.debug) {
        console.log('[MetricTracker] Hash mode: listening to hashchange events');
      }
    } else {
      // history 模式：监听 popstate 和拦截 pushState/replaceState
      window.addEventListener('popstate', () => {
        this.handleRouteChange('popstate');
      });

      // 监听 pushState 和 replaceState（SPA 路由变化）
      const originalPushState = history.pushState;
      const originalReplaceState = history.replaceState;

      history.pushState = (...args) => {
        originalPushState.apply(history, args);
        this.handleRouteChange('pushState');
      };
      history.replaceState = (...args) => {
        originalReplaceState.apply(history, args);
        this.handleRouteChange('replaceState');
      };

      if (this.config.debug) {
        console.log('[MetricTracker] History mode: listening to popstate/pushState/replaceState events');
      }
    }
  }

  /**
   * 处理路由变化
   */
  private async handleRouteChange(eventType: string): Promise<void> {
    if (!this.isStarted) {
      return;
    }

    const currentPathname = this.getCurrentPath();

    // 只有当路径真正改变时才处理
    if (currentPathname === this.lastPathname) {
      return;
    }

    if (this.config.debug) {
      console.log('[MetricTracker] Route changed:', {
        eventType,
        from: this.lastPathname,
        to: currentPathname,
      });
    }

    // 先更新 lastPathname，防止后续 replaceState 触发重复处理
    this.lastPathname = currentPathname;

    // 检查 URL 是否有 pc 参数
    const urlParams = new URLSearchParams(window.location.search);
    const existingPc = urlParams.get('pc');

    // 如果有待添加的 pc 参数，且 URL 中还没有 pc 参数
    if (this.pendingPc && !existingPc) {
      if (this.config.debug) {
        console.log('[MetricTracker] Adding pending pc to URL:', this.pendingPc);
      }

      // 添加 pc 参数到 URL
      urlParams.set('pc', this.pendingPc);
      const newUrl = `${window.location.pathname}?${urlParams.toString()}${window.location.hash}`;

      // 使用 replaceState 更新 URL（由于 lastPathname 已更新，不会触发重复处理）
      history.replaceState(history.state, '', newUrl);

      if (this.config.debug) {
        console.log('[MetricTracker] URL updated with pc parameter:', newUrl);
      }
    }

    // 清除待添加的 pc
    this.pendingPc = null;

    // 更新当前位置码
    await this.updateCurrentPositionCode();

    // 收集新页面 URL 参数中的位置码
    const urlEvent = this.urlParamCollector.collect();
    if (urlEvent) {
      this.handleEvent(urlEvent);
    }
  }

  /**
   * 处理收集到的事件
   */
  private handleEvent(event: PositionCodeEvent): void {
    if (!this.isStarted) {
      if (this.config.debug) {
        console.warn('[MetricTracker] Not started, event ignored:', event);
      }
      return;
    }

    // 添加到上报队列
    this.reporter.addEvent(event);
  }
}
